/**
 * Airport transfers — the route and pickup information RECORDED in a booking
 * (server: booking.transfer_route + booking.details). Never deduced from the
 * listing title. Same checks as backend/services/transfer_booking.py, so the
 * form can explain a problem before sending; the server checks again.
 */
export const AIRPORT_ZONE_ID = 'zone_phuket_airport';
export const DIRECTION_ZONE_TO_ZONE = 'zone_to_zone';   // e.g. Patong → Ao Nang (no airport)

/** Direction of a route, same rule as the server (transfer_booking.route_direction). */
export const routeDirection = (from, to) => (from === AIRPORT_ZONE_ID ? 'from_airport'
  : to === AIRPORT_ZONE_ID ? 'to_airport' : DIRECTION_ZONE_TO_ZONE);

/**
 * Routes a transfer listing offers, with their price: the server's own list
 * (service.transfer_routes: airport zones and Phuket ↔ Krabi, both ways); an
 * older response without it → the airport zones both ways.
 */
export function listingRoutes(service) {
  if (Array.isArray(service?.transfer_routes)) return service.transfer_routes;
  const routes = [];
  ((((service || {}).details || {}).transport || {}).airport_transfers || []).forEach((z) => {
    if (!z?.zone_id) return;
    const out = z.price_from_airport ?? z.price;
    const back = z.price_to_airport ?? z.price;
    if (Number(out) > 0) routes.push({ from_zone_id: AIRPORT_ZONE_ID, to_zone_id: z.zone_id, price: Number(out), direction: 'from_airport' });
    if (Number(back) > 0) routes.push({ from_zone_id: z.zone_id, to_zone_id: AIRPORT_ZONE_ID, price: Number(back), direction: 'to_airport' });
  });
  return routes;
}

export const findRoute = (routes, from, to) => (from && to
  ? (routes || []).find((r) => r.from_zone_id === from && r.to_zone_id === to) || null : null);

/** Departures offered (the airport first, then in the server's order). */
export function departureChoices(routes) {
  const ids = [];
  (routes || []).forEach((r) => { if (!ids.includes(r.from_zone_id)) ids.push(r.from_zone_id); });
  return ids.sort((a, b) => (b === AIRPORT_ZONE_ID) - (a === AIRPORT_ZONE_ID));
}

/** Destinations offered from a departure, each with its price. */
export const destinationChoices = (routes, from) => (routes || []).filter((r) => r.from_zone_id === from);
export const MAX_LUGGAGE = 30;
export const MAX_TEXT = 200;

/** Zone name to display: the airport in the reader's language, other zones by name. */
export const zoneLabel = (zoneId, name, airportLabel) => (zoneId === AIRPORT_ZONE_ID ? airportLabel : (name || zoneId || ''));

/** The booked route {from, to} or null (old bookings without a route). */
export function bookingRoute(booking) {
  const r = booking?.transfer_route;
  if (r?.from_zone_id && r?.to_zone_id) return r;
  const d = booking?.details || {};
  if (d.from_zone_id && d.to_zone_id) {
    return { from_zone_id: d.from_zone_id, to_zone_id: d.to_zone_id, from_zone_name: d.from_zone_name, to_zone_name: d.to_zone_name,
      direction: d.direction || (d.from_zone_id === AIRPORT_ZONE_ID ? 'from_airport' : 'to_airport') };
  }
  return null;
}

/** "Aéroport de Phuket → Patong" (null without a route). */
export function routeText(booking, airportLabel) {
  const r = bookingRoute(booking);
  if (!r) return null;
  return `${zoneLabel(r.from_zone_id, r.from_zone_name, airportLabel)} → ${zoneLabel(r.to_zone_id, r.to_zone_name, airportLabel)}`;
}

export const normalizeFlight = (s) => String(s || '').replace(/[\s-]/g, '').toUpperCase();

/**
 * Checks of the pickup form → { field: errorKey } (empty when valid).
 * Keys are i18n keys under transferBooking.errors.
 */
/**
 * Capacity declared for the listing's vehicle (null when not declared: no
 * limit is invented). Same rule as the server (transfer_booking.vehicle_capacity).
 */
export function vehicleCapacity(service) {
  const v = (((service || {}).details || {}).transport || {}).vehicle || {};
  const num = (x, min) => {
    const n = typeof x === 'boolean' ? NaN : Number(String(x ?? '').trim());
    return String(x ?? '').trim() !== '' && Number.isInteger(n) && n >= min ? n : null;
  };
  return { passengersMax: num(v.passengers_max, 1), luggageMax: num(v.luggage_max, 0) };
}

export const MAX_ROOM = 20;

/**
 * Google Maps link of the hotel (hotel → airport), same rule as the server
 * (transfer_booking.clean_maps_url): share links (maps.app.goo.gl, goo.gl/maps,
 * g.co/kgs, share.google, g.page) and Google Maps URLs on any Google domain.
 * A pasted share text keeps only its link. → https URL, or null if not valid.
 */
export function cleanMapsUrl(value) {
  const raw = String(value || '').trim();
  const found = /https?:\/\/[^\s<>"']+/i.exec(raw);
  const text = (found ? found[0] : raw).replace(/[.,;)]+$/, '');
  let url;
  try { url = new URL(text); } catch { return null; }
  const host = url.hostname.toLowerCase();
  const path = url.pathname || '';
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || text.length > 500) return null;
  const share = ['maps.app.goo.gl', 'share.google', 'g.page'].includes(host)
    || (host === 'goo.gl' && path.startsWith('/maps'))
    || (host === 'g.co' && path.startsWith('/kgs/'));
  const google = /^maps\.google\.(com|[a-z]{2,3})(\.[a-z]{2})?$/.test(host)
    || (/^(www\.)?google\.(com|[a-z]{2,3})(\.[a-z]{2})?$/.test(host) && path.startsWith('/maps'));
  if (!share && !google) return null;
  if (share && path.replace(/^\/+|\/+$/g, '').length < 2) return null;
  url.protocol = 'https:';
  return url.toString();
}

export function validatePickup({ direction, luggage, place, flightNumber, flightArrival, luggageMax = null,
  room = '', roomUnknown = false, mapsUrl = '', dropoff = '' }) {
  const errors = {};
  const bags = String(luggage ?? '').trim();
  if (!/^\d+$/.test(bags) || Number(bags) > MAX_LUGGAGE) errors.luggage = 'luggageInvalid';
  else if (luggageMax !== null && Number(bags) > luggageMax) errors.luggage = 'luggageOverCapacity';
  const text = String(place || '').trim();
  if (!text) errors.place = direction === 'from_airport' ? 'dropoffRequired' : 'pickupRequired';
  else if (text.length > MAX_TEXT) errors.place = 'placeTooLong';
  const flight = normalizeFlight(flightNumber);
  if (flight && !/^[A-Z0-9]{2,3}\d{1,4}[A-Z]?$/.test(flight)) errors.flightNumber = 'flightInvalid';
  if (direction === 'from_airport') {
    // Arrival: flight number and scheduled LANDING time required (the driver
    // meeting time is the booking time, a separate field).
    if (!flight) errors.flightNumber = 'flightRequired';
    if (!flightArrival) errors.flightArrival = 'arrivalRequired';
    else if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(flightArrival)) errors.flightArrival = 'arrivalInvalid';
  } else if (direction === DIRECTION_ZONE_TO_ZONE) {
    // Zone → zone: the exact destination address too (no flight, no room).
    const to = String(dropoff || '').trim();
    if (!to) errors.dropoff = 'dropoffRequired';
    else if (to.length > MAX_TEXT) errors.dropoff = 'placeTooLong';
  } else if (direction === 'to_airport') {
    // Hotel → airport: room number (unless not known yet) and the hotel's Google Maps link.
    const roomText = String(room || '').trim();
    if (!roomUnknown && !roomText) errors.room = 'roomRequired';
    else if (roomText.length > MAX_ROOM) errors.room = 'roomTooLong';
    if (!String(mapsUrl || '').trim()) errors.mapsUrl = 'mapsRequired';
    else if (!cleanMapsUrl(mapsUrl)) errors.mapsUrl = 'mapsInvalid';
  }
  return errors;
}

/** Instant (ms, UTC) of a date + time typed in Phuket local time (UTC+7, no DST). */
export function phuketInstant(date, time) {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  const h = /^(\d{2}):(\d{2})$/.exec(time || '');
  if (!d || !h) return null;
  return Date.UTC(+d[1], +d[2] - 1, +d[3], +h[1] - 7, +h[2]);
}

/** The provider's minimum notice (hours) for this direction, from the listing. */
export function minNoticeHours(service, direction) {
  const rules = service?.transfer_booking_rules;
  if (!rules) return null;
  if (direction === DIRECTION_ZONE_TO_ZONE) return rules.min_notice_zone_to_zone_hours ?? 6;
  return direction === 'from_airport' ? rules.min_notice_from_airport_hours : rules.min_notice_to_airport_hours;
}

/**
 * Hours of notice the pickup misses (null when fine or unknown). Hint only:
 * the server checks again against its own clock when the request is sent.
 */
export function minNoticeViolation({ date, time, hours, now = Date.now() }) {
  if (!hours) return null;
  const pickup = phuketInstant(date, time);
  if (pickup === null) return null;
  return pickup - now < hours * 3600 * 1000 ? hours : null;
}

// Airport → hotel: the pickup is planned this long after the scheduled landing
// (computed again by the server; not a guarantee if the flight is late).
export const PICKUP_AFTER_LANDING_MINUTES = 45;

/** Landing (YYYY-MM-DD, HH:MM, Phuket time) → pickup { date, time }, next day after a late landing. */
export function pickupAfterLanding(date, time) {
  const at = phuketInstant(date, time);
  if (at === null) return null;
  const local = new Date(at + 7 * 3600 * 1000 + PICKUP_AFTER_LANDING_MINUTES * 60 * 1000);   // UTC fields = Phuket time
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`,
    time: `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`,
  };
}

/** "15:30" → "15 h 30" in French, unchanged in English. */
export const clockLabel = (time, lang) => {
  const m = /^(\d{2}):(\d{2})$/.exec(time || '');
  if (!m) return time || '';
  return String(lang || '').startsWith('fr') ? `${m[1]} h ${m[2]}` : time;
};

/**
 * Vehicle capacity typed by the provider in a listing form: a whole number
 * ≥ min, else null (empty, decimal, negative or not a number). No ceiling:
 * the provider declares the vehicle's real capacity (server: check_listing_capacity).
 */
export function parseCapacity(value, min) {
  const text = String(value ?? '').trim();
  return /^\d+$/.test(text) && Number(text) >= min ? Number(text) : null;
}
